import React from 'react';
import { Building2, SearchX } from 'lucide-react';

import { cn } from '../../lib/utils';

/**
 * A live miniature of the customer form.
 *
 * This exists so the settings page doesn't have to describe what each colour
 * does — you change it, you see it. `highlight` names the colour currently
 * being edited, and the parts of the form it drives are ringed, which answers
 * "what is 'On accent' for?" without a sentence of copy.
 *
 * It approximates the real form rather than embedding it: same four colours in
 * the same roles, same derived surfaces (see the customer app's
 * lib/branding.js, which this mirrors).
 */

const DEFAULTS = {
  accent: '#02046e',
  accentText: '#ffffff',
  background: '#f2f3f5',
  text: '#162139'
};

/** Keep in step with STEPS in the customer app's src/steps.js. */
const STEP_COUNT = 6;

const Ring = ({ active, className, children, ...props }) => (
  <div
    className={cn(
      'transition-shadow rounded-md',
      active && 'ring-2 ring-offset-1 ring-sky-500 ring-offset-transparent',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

const QuoteFormPreview = ({ colours = {}, logoUrl, shopName, accepting = true, highlight }) => {
  const accent = colours.accent || DEFAULTS.accent;
  const accentText = colours.accentText || DEFAULTS.accentText;
  const background = colours.background || DEFAULTS.background;
  const text = colours.text || DEFAULTS.text;

  const surface = `color-mix(in oklab, ${text} 8%, ${background})`;
  const subdued = `color-mix(in oklab, ${text} 62%, ${background})`;
  const hairline = `color-mix(in oklab, ${text} 12%, ${background})`;

  return (
    <div
      className="w-full overflow-hidden rounded-2xl shadow-lg select-none"
      style={{ backgroundColor: background, color: text }}
      aria-label="Preview of the customer form"
    >
      <Ring active={highlight === 'background'} className="rounded-none">
        {accepting ? (
          <div className="flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-2 px-4 pt-4" style={{ borderColor: hairline }}>
              {logoUrl ? (
                <img src={logoUrl} alt="" className="h-7 w-auto max-w-24 object-contain" />
              ) : (
                <span
                  className="flex h-7 w-7 items-center justify-center rounded-lg"
                  style={{ backgroundColor: accent, color: accentText }}
                >
                  <Building2 className="h-3.5 w-3.5" />
                </span>
              )}
              <Ring active={highlight === 'text'}>
                <span className="truncate text-sm font-semibold">{shopName || 'Your shop'}</span>
              </Ring>
            </div>

            {/* Progress */}
            <div className="px-4 pt-3">
              <Ring active={highlight === 'accent'} className="flex items-center gap-1">
                {Array.from({ length: STEP_COUNT }, (_, index) => (
                  <span
                    key={index}
                    className="h-1 flex-1 rounded-full"
                    style={{ backgroundColor: index === 0 ? accent : surface }}
                  />
                ))}
              </Ring>
              <p className="mt-1.5 text-[10px]" style={{ color: subdued }}>Step 1 of {STEP_COUNT}</p>
            </div>

            {/* Body */}
            <div className="flex flex-col gap-3 px-4 pb-4 pt-4">
              <Ring active={highlight === 'text'}>
                <p className="text-base font-semibold tracking-tight">Your details</p>
              </Ring>

              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-medium">First name</span>
                <div className="h-7 rounded-md px-2 text-[11px] leading-7" style={{ backgroundColor: surface, color: subdued }}>
                  Jane
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-medium">Mobile number</span>
                <div className="h-7 rounded-md px-2 text-[11px] leading-7" style={{ backgroundColor: surface, color: subdued }}>
                  021 123 4567
                </div>
              </div>

              <Ring active={highlight === 'accent' || highlight === 'accentText'} className="mt-1">
                <div
                  className="flex h-9 items-center justify-center rounded-md text-xs font-medium"
                  style={{ backgroundColor: accent, color: accentText }}
                >
                  <Ring active={highlight === 'accentText'} className="px-1">
                    Start my request
                  </Ring>
                </div>
              </Ring>

            </div>
          </div>
        ) : (
          /* What a customer sees when the form is closed */
          <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
            <span
              className="flex h-10 w-10 items-center justify-center rounded-full"
              style={{ backgroundColor: surface, color: subdued }}
            >
              <SearchX className="h-5 w-5" />
            </span>
            <p className="text-sm font-semibold">We couldn't open that link</p>
            <p className="text-[11px] leading-relaxed" style={{ color: subdued }}>
              Please get in touch with the repairer directly to arrange your quote.
            </p>
          </div>
        )}
      </Ring>
    </div>
  );
};

export { DEFAULTS as PREVIEW_DEFAULTS };
export default QuoteFormPreview;
